import { Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { DataForDisplayingMenuItemsService } from '../../data-for-displaying-menu-items.service';
import { environment } from '../../../../environments/environment';
import { StageService } from '../../stage.service';
import { Subject } from 'rxjs/Subject';
import { ConstantsService } from '../../constants.service';
import { RailingMaterialService } from './railing-material.service';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';

declare var THREE: any;

@Injectable()
export class RailingColorsService {
  private colorUrl: string = environment.apiUrl + '/colors';
  private colorGroupUrl: string = environment.apiUrl + '/ColorGroups';
  addProductType = new Subject<any>();
  setColorsToComponent = new Subject<any>();
  colorGroups;
  loader = new THREE.TextureLoader();
  colorMap = {};
  private modelsChildrenNamesForColorChange;

  constructor(
    private http: HttpClient,
    private dataForMenu: DataForDisplayingMenuItemsService,
    public stage: StageService,
    private constantsService: ConstantsService,
    private railingMaterialSrvc: RailingMaterialService
  ) {
    this.getColorGroups().subscribe(colorGroups => {
      this.colorGroups = colorGroups;
    });
    this.modelsChildrenNamesForColorChange = this.constantsService.modelsChildrenNamesForColorChange;
  }

  private _textureTintCache: { [url: string]: Promise<any> } = {};

  // Average color of a color-swatch texture (they are solid/near-solid 512px
  // images). Used to tint keepAlphaMap panels, whose diffuse map holds the
  // perforation pattern and must not be replaced by the swatch texture.
  private _tintMaterialFromTexture(material, textureUrl: string) {
    if (!this._textureTintCache[textureUrl]) {
      this._textureTintCache[textureUrl] = new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = 1;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, 1, 1);
          const d = ctx.getImageData(0, 0, 1, 1).data;
          resolve(new THREE.Color(d[0] / 255, d[1] / 255, d[2] / 255));
        };
        img.onerror = () => resolve(null);
        img.src = textureUrl;
      });
    }
    this._textureTintCache[textureUrl].then(color => {
      if (color) {
        material.color.copy(color);
        material.needsUpdate = true;
      }
    });
  }

  private _applyColorToMaterial(material, materialConfig, selected) {
    material.color.copy(materialConfig.color);
    if (material.userData && material.userData.keepAlphaMap) {
      // keepAlphaMap: local placeholder laser panels carry their perforation
      // in the diffuse map — colors may only tint them. Texture-only colors
      // (no rgb) are approximated by the swatch texture's average color.
      if (selected && selected.texture && !selected.rgb) {
        this._tintMaterialFromTexture(material, selected.texture);
      }
    } else {
      material.map = materialConfig.texture;
    }
    material.needsUpdate = true;
  }

  private _getMaterialConf(selectedConfigs) {
    return {
      color: new THREE.Color(selectedConfigs.rgb || 0xffffff),
      texture: selectedConfigs.texture
        ? this.loader.load(selectedConfigs.texture, texture => {
            texture.wrapT = texture.wrapS = THREE.RepeatWrapping;
            texture.needsUpdate = true;
          })
        : null
    };
  }

  getColors(): Observable<any> {
    const correctPathToColorsTexture = colors => {
      if (colors && colors.length) {
        colors.forEach(color => {
          if (color.texture) {
            if (environment.production) {
              color.texture = color.texture
                .replace('upload', 'download')
                .replace('138.197.129.42:9000', 'feeney3d.com')
                .replace('feeney.interiordesigntools.com', 'feeney3d.com')
                .replace('http:', 'https:');
            } else {
              color.texture = color.texture
                .replace('upload', 'download')
                .replace('138.197.129.42:9000', 'feeney.interiordesigntools.com')
                .replace('feeney3d.com', 'feeney.interiordesigntools.com')
                .replace('http:', 'https:');
            }
          }
        });
      }
    };
    return this.http.get<any>(this.colorUrl).pipe(
      switchMap(colors => {
        correctPathToColorsTexture(colors);
        return of(colors);
      })
    );
  }

  setColors(colors) {
    this.setColorsToComponent.next(colors);
  }

  getColorGroups(): Observable<any> {
    return this.http.get<any>(this.colorGroupUrl);
  }

  getColorGroupById(colorGroupId) {
    let colorGroup;
    this.colorGroups.forEach(el => {
      if (el.id === colorGroupId) {
        colorGroup = el;
      }
    });
    return colorGroup;
  }

  updateColorMap(colorMap) {
    // console.log('this.colorMap', this.colorMap);
    this.colorMap = colorMap;
  }

  setExistingColor(obj, typeToColor: string, nameOfObj?) {
    this.addProductType.next({
      productType: obj.userData.entity.productType,
      colorType: typeToColor
    });

    const railingType = this.dataForMenu.currentChoises.railingType;
    // the color map may have been reset (new project / mode switch) while the
    // models of an earlier build were still loading — nothing to color then
    if (!this.colorMap[railingType]) {
      return;
    }
    const elements = this.colorMap[railingType].railingElements[typeToColor];

    obj.children.forEach(el => {
      if (el.name === 'TopCap') {
        el.material = el.material.clone();
        obj.children.forEach(el => {
          if (el.name === 'Handrail') {
            el.material = el.material.clone();
          }
        });
      }
    });

    if (elements && elements.productTypes.includes(obj.userData.entity.productType)) {
      this.dataForMenu.setRailingColor(elements);
      const { selected } = elements;
      const materialConfig = this._getMaterialConf(selected);

      for (let j = 0; j < obj.children.length; j++) {
        if (
          this.modelsChildrenNamesForColorChange[typeToColor].some((name: string) =>
            name.toLowerCase().includes(obj.children[j].name.toLowerCase())
          )
        ) {
          const child = obj.children[j];
          this._applyColorToMaterial(child.material, materialConfig, selected);
          this.railingMaterialSrvc.configMaterial(
            child,
            typeToColor,
            railingType,
            elements.selected
          );
        }
      }
    }
  }

  syncObjColors(obj) {
    const { entity } = obj.userData;

    if (!entity) return;
    console.log('this.colorMap', this.colorMap);
    this.constantsService.objsWhichColorsSyncedWithThisColor.forEach(objConf => {
      if (objConf[entity.productType]) {
        const type = objConf.color;
        const railingType = this.dataForMenu.currentChoises.railingType;
        if (!this.colorMap[railingType]) {
          return;
        }
        const elements = this.colorMap[railingType].railingElements[type];
        if (elements) {
          const {selected} = elements;
          const materialConfig = this._getMaterialConf(selected);

          obj.children.forEach(c => {
            if (c && objConf[entity.productType].includes(c.name)) {
              this._applyColorToMaterial(c.material, materialConfig, selected);
              const {tuneMaterial} = objConf;
              this.railingMaterialSrvc[tuneMaterial](c);
            }
          });
        }
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/railing/services/railing-colors.service.ts